import React, { useState, useEffect } from 'react';
/* global axios */
import api from '../services/apiService';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';

const EmployeeForm = () => {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  // Prüfe, ob im Erstellungsmodus (neuer Mitarbeiter)
  const isNew = !employeeId || employeeId === 'new';

  // Zustände für Mitarbeiterfelder
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [role, setRole] = useState('Fahrer');
  const [licenseClasses, setLicenseClasses] = useState('');
  const [yearsOfExperience, setYearsOfExperience] = useState('');
  const [specialCertifications, setSpecialCertifications] = useState('');
  const [status, setStatus] = useState('verfügbar'); // Nur zur Anzeige
  const [sortOrder, setSortOrder] = useState('');

  // Tab-Management
  const [activeTab, setActiveTab] = useState('general');

  // States für Abwesenheiten
  const [vacations, setVacations] = useState([]);
  const [sickLeaves, setSickLeaves] = useState([]);
  // States für neue Abwesenheiten - Urlaub
  const [newVacationStart, setNewVacationStart] = useState('');
  const [newVacationEnd, setNewVacationEnd] = useState('');
  const [newVacationReason, setNewVacationReason] = useState('');
  const [newVacationApprovedBy, setNewVacationApprovedBy] = useState('');
  // States für neue Abwesenheiten - Krank
  const [newSickStart, setNewSickStart] = useState('');
  const [newSickEnd, setNewSickEnd] = useState('');
  const [newSickReason, setNewSickReason] = useState('');
  const [newSickApprovedBy, setNewSickApprovedBy] = useState('');

  // Zustände für Lade- und Fehlermeldungen
  const [loading, setLoading] = useState(!isNew); // Im Erstellungsmodus: kein initiales Laden
  const [error, setError] = useState(null);

  // Pagination-Konfiguration
  const entriesPerPage = 16;
  const [vacationPage, setVacationPage] = useState(1);
  const [sickPage, setSickPage] = useState(1);

  // Berechnung für Pagination der Urlaube
  const sortedVacations = vacations.slice().sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
  const indexOfLastVacation = vacationPage * entriesPerPage;
  const indexOfFirstVacation = indexOfLastVacation - entriesPerPage;
  const currentVacations = sortedVacations.slice(indexOfFirstVacation, indexOfLastVacation);
  const totalVacationPages = Math.ceil(sortedVacations.length / entriesPerPage);

  // Berechnung für Pagination der Krankmeldungen
  const sortedSickLeaves = sickLeaves.slice().sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
  const indexOfLastSick = sickPage * entriesPerPage;
  const indexOfFirstSick = indexOfLastSick - entriesPerPage;
  const currentSickLeaves = sortedSickLeaves.slice(indexOfFirstSick, indexOfLastSick);
  const totalSickPages = Math.ceil(sortedSickLeaves.length / entriesPerPage);

  // Funktion zum Neuladen des Mitarbeiters (Cache-Busting)
  const reloadEmployee = async () => {
    if (isNew) {
      console.error('reloadEmployee: Neuer Mitarbeiter, kein Reload notwendig');
      return;
    }
    try {
      const response = await api.get(
        `/employees/${employeeId}?_=${new Date().getTime()}`
      );
      console.log('Aktuell geladener Mitarbeiter:', response.data);
      // Aktualisiere Felder, falls benötigt
      return response.data;
    } catch (error) {
      console.error('Fehler beim Neuladen des Mitarbeiters:', error);
    }
  };

  // Laden der Mitarbeiterdaten (nur im Bearbeitungsmodus)
  useEffect(() => {
    if (!isNew) {
      api
        .get(`/employees/${employeeId}`)
        .then(response => {
          const emp = response.data;
          setFirstName(emp.firstName || '');
          setLastName(emp.lastName || '');
          setPhoneNumber(emp.phoneNumber || '');
          setEmail(emp.email || '');
          setAddress(emp.address || '');
          setRole(emp.role || 'Fahrer');
          setLicenseClasses(emp.licenseClasses ? emp.licenseClasses.join(', ') : '');
          setYearsOfExperience(emp.yearsOfExperience || '');
          setSpecialCertifications(emp.specialCertifications ? emp.specialCertifications.join(', ') : '');
          setStatus(emp.status || 'verfügbar');
          setLoading(false);
          setSortOrder(emp.sortOrder || '');
        })
        .catch(err => {
          setError('Fehler beim Laden der Mitarbeiterdaten');
          setLoading(false);
        });
    }
  }, [employeeId, isNew]);

  // Beim Erstellen (Neuanlage) alle Felder initialisieren
  useEffect(() => {
    if (isNew) {
      setFirstName('');
      setLastName('');
      setPhoneNumber('');
      setEmail('');
      setAddress('');
      setRole('Fahrer');
      setLicenseClasses('');
      setYearsOfExperience('');
      setSpecialCertifications('');
      setStatus('verfügbar');
      setVacations([]);
      setSickLeaves([]);
      setLoading(false);
      setError(null);
      setSortOrder('');
    }
  }, [isNew]);

  // Laden der Abwesenheiten (nur im Bearbeitungsmodus)
  useEffect(() => {
    if (!isNew) {
      api
        .get('/employees-absence')
        .then(response => {
          const allAbsences = response.data.filter(
            absence => absence.employeeId === parseInt(employeeId, 10)
          );
          setVacations(allAbsences.filter(a => a.type === 'urlaub'));
          setSickLeaves(allAbsences.filter(a => a.type === 'krank'));
        })
        .catch(err => {
          console.error('Fehler beim Laden der Abwesenheiten:', err);
        });
    }
  }, [employeeId, isNew]);

  // Handler zum Hinzufügen eines neuen Urlaubs
  const handleAddVacation = () => {
    if (!newVacationStart || !newVacationEnd) {
      alert('Bitte Start- und Enddatum eingeben');
      return;
    }
    const newVacation = {
      employeeId: parseInt(employeeId, 10),
      type: 'urlaub',
      startDate: newVacationStart,
      endDate: newVacationEnd,
      reason: newVacationReason,
      approvedBy: newVacationApprovedBy ? parseInt(newVacationApprovedBy, 10) : null,
    };
    api
      .post('/employees-absence', newVacation)
      .then(response => {
        setVacations(prev => [...prev, response.data]);
        setNewVacationStart('');
        setNewVacationEnd('');
        setNewVacationReason('');
        setNewVacationApprovedBy('');
        // Reload Employee, um aktuellen Status zu übernehmen
        reloadEmployee();
      })
      .catch(err => {
        console.error('Fehler beim Hinzufügen des Urlaubs:', err);
        alert('Fehler beim Hinzufügen des Urlaubs');
      });
  };

  // Handler zum Hinzufügen einer neuen Krankmeldung
  const handleAddSickLeave = () => {
    if (!newSickStart || !newSickEnd) {
      alert('Bitte Start- und Enddatum eingeben');
      return;
    }
    const newSick = {
      employeeId: parseInt(employeeId, 10),
      type: 'krank',
      startDate: newSickStart,
      endDate: newSickEnd,
      reason: newSickReason,
      approvedBy: newSickApprovedBy ? parseInt(newSickApprovedBy, 10) : null,
    };
    api
      .post('/employees-absence', newSick)
      .then(response => {
        setSickLeaves(prev => [...prev, response.data]);
        setNewSickStart('');
        setNewSickEnd('');
        setNewSickReason('');
        setNewSickApprovedBy('');
        // Reload Employee, um aktuellen Status zu übernehmen
        reloadEmployee();
      })
      .catch(err => {
        console.error('Fehler beim Hinzufügen der Krankmeldung:', err);
        alert('Fehler beim Hinzufügen der Krankmeldung');
      });
  };

  // Handler zum Löschen eines Abwesenheitseintrags
  const handleDeleteAbsence = (absenceId, type) => {
    if (!employeeId) {
      console.error('handleDeleteAbsence: employeeId ist undefined');
      return;
    }
    if (window.confirm('Soll der Eintrag wirklich gelöscht werden?')) {
      api
        .delete(`/employees-absence/${absenceId}`)
        .then(() => {
          if (type === 'urlaub') {
            setVacations(prev => prev.filter(a => a.id !== absenceId));
          } else if (type === 'krank') {
            setSickLeaves(prev => prev.filter(a => a.id !== absenceId));
          }
          setTimeout(() => {
            reloadEmployee();
          }, 500);
        })
        .catch(err => {
          console.error('Fehler beim Löschen des Eintrags:', err);
          alert('Fehler beim Löschen des Eintrags');
        });
    }
  };

  // Handler für das Speichern (Erstellen/Aktualisieren)
  const handleSubmit = async (e) => {
    e.preventDefault();
    const employeeData = {
      firstName,
      lastName,
      phoneNumber,
      email,
      address,
      role,
      licenseClasses: licenseClasses.split(',').map(s => s.trim()),
      yearsOfExperience: parseInt(yearsOfExperience, 10),
      specialCertifications: specialCertifications.split(',').map(s => s.trim()),
      sortOrder: parseInt(sortOrder, 10),
    };

    try {
      if (!isNew) {
        await api.put(`/employees/${employeeId}`, employeeData);
        alert('Mitarbeiter erfolgreich aktualisiert!');
      } else {
        await api.post('/employees', employeeData);
        alert('Mitarbeiter erfolgreich hinzugefügt!');
      }
      navigate('/employeelist');
    } catch (err) {
      console.error('Fehler beim Speichern des Mitarbeiters:', err);
      alert('Fehler beim Speichern des Mitarbeiters');
    }
  };

  if (loading) return <div>Lade Mitarbeiterdaten...</div>;
  if (error) return <div>{error}</div>;

  return (
    <form className="form" onSubmit={handleSubmit}>
      <Helmet>
        <title>Mitarbeiter {isNew ? 'anlegen' : 'bearbeiten'}</title>
        <meta name="description" content={isNew ? 'Neuen Mitarbeiter anlegen' : 'Mitarbeiter bearbeiten'} />
      </Helmet>
      <h2>{isNew ? 'Neuen Mitarbeiter anlegen' : 'Mitarbeiter bearbeiten'}</h2>

      {/* Tab-Leiste */}
      <div className="tabs">
        {[
          { id: 'general', label: 'Allgemein' },
          { id: 'info1', label: 'Urlaube' },
          { id: 'info2', label: 'Krankmeldungen' },
        ].map(tab => (
          <button
            key={tab.id}
            type="button"
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
            aria-pressed={activeTab === tab.id}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab-Inhalte */}
      {activeTab === 'general' && (
        <div className="form-row">
          <div className="col">
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Vorname*"
              required
            />
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Nachname*"
              required
            />
            <input
              type="text"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="Mobiltelefon* z.B. +491772987845"
            />
          </div>
          <div className="col">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="E-Mail*"
              required
            />
            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Adresse"
            />
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              style={{ width: '100%', marginLeft: '0px', marginTop: '0px', padding: '10px', fontSize: '1em' }}
            >
              <option value="Fahrer">Fahrer</option>
              <option value="Service">Service</option>
              <option value="Hilfskraft">Hilfskraft</option>
              <option value="Disponent">Disponent</option>
              <option value="Vertrieb">Vertrieb</option>
              
            </select>
            <input
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              placeholder="Sortierung*"
              
            />
          </div>
          <div className="col">
            <input
              type="text"
              value={licenseClasses}
              onChange={(e) => setLicenseClasses(e.target.value)}
              placeholder="Führerscheinklassen (kommagetrennt)"
            />
            <input
              type="number"
              value={yearsOfExperience}
              onChange={(e) => setYearsOfExperience(e.target.value)}
              placeholder="Erfahrung (Jahre)*"
            />
            <input
              type="text"
              value={specialCertifications}
              onChange={(e) => setSpecialCertifications(e.target.value)}
              placeholder="Spezielle Zertifikate (kommagetrennt)"
            />
            {/* Status nur anzeigen */}
            <input
              type="text"
              value={status}
              disabled
              style={{ backgroundColor: '#eee' }}
            />
          </div>
        </div>
      )}

      {activeTab === 'info1' && (
        <div className="form-row" style={{ display: 'flex', gap: '20px' }}>
          {/* Linke Spalte: Formular für Urlaube */}
          <div className="col" style={{ flex: 1 }}>
            <h3>Neuen Urlaub hinzufügen</h3>
            <div>
              <input
                type="date"
                value={newVacationStart}
                onChange={(e) => setNewVacationStart(e.target.value)}
                required
              />
              <input
                type="date"
                value={newVacationEnd}
                onChange={(e) => setNewVacationEnd(e.target.value)}
                required
              />
              <input
                type="text"
                value={newVacationReason}
                onChange={(e) => setNewVacationReason(e.target.value)}
                placeholder="Grund (optional)"
              />
              <input
                type="number"
                value={newVacationApprovedBy}
                onChange={(e) => setNewVacationApprovedBy(e.target.value)}
                placeholder="Genehmigt von (ID, optional)"
              />
              <button type="button" onClick={handleAddVacation}>
                Urlaub hinzufügen
              </button>
            </div>
          </div>
          {/* Rechte Spalte: Tabelle der Urlaube mit Paginierung */}
          <div className="col" style={{ flex: 2 }}>
            <h3>Erfasste Urlaube</h3>
            {!isNew ? (
              <>
                {vacations.length > 0 ? (
                  <>
                    <table style={{ width: '100%' }}>
                      <thead>
                        <tr>
                          <th>Startdatum</th>
                          <th>Enddatum</th>
                          <th>Grund</th>
                          <th>Genehmigt von</th>
                          <th>Aktionen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentVacations.map(vac => (
                          <tr key={vac.id}>
                            <td>{new Date(vac.startDate).toLocaleDateString()}</td>
                            <td>{new Date(vac.endDate).toLocaleDateString()}</td>
                            <td>{vac.reason || '-'}</td>
                            <td>{vac.approvedBy || '-'}</td>
                            <td>
                              <button type="button" onClick={() => handleDeleteAbsence(vac.id, 'urlaub')}>
                                Löschen
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'space-between' }}>
                      <button
                        type="button"
                        onClick={() => setVacationPage(prev => Math.max(prev - 1, 1))}
                        disabled={vacationPage === 1}
                      >
                        Vorherige Seite
                      </button>
                      <span>
                        Seite {vacationPage} von {totalVacationPages}
                      </span>
                      <button
                        type="button"
                        onClick={() => setVacationPage(prev => Math.min(prev + 1, totalVacationPages))}
                        disabled={vacationPage === totalVacationPages}
                      >
                        Nächste Seite
                      </button>
                    </div>
                  </>
                ) : (
                  <p>Keine Urlaube erfasst.</p>
                )}
              </>
            ) : (
              <p>Abwesenheiten können erst erfasst werden, wenn der Mitarbeiter gespeichert wurde.</p>
            )}
          </div>
        </div>
      )}

      {activeTab === 'info2' && (
        <div className="form-row" style={{ display: 'flex', gap: '20px' }}>
          {/* Linke Spalte: Formular für Krankmeldungen */}
          <div className="col" style={{ flex: 1 }}>
            <h3>Neue Krankmeldung hinzufügen</h3>
            <div>
              <input
                type="date"
                value={newSickStart}
                onChange={(e) => setNewSickStart(e.target.value)}
                required
              />
              <input
                type="date"
                value={newSickEnd}
                onChange={(e) => setNewSickEnd(e.target.value)}
                required
              />
              <input
                type="text"
                value={newSickReason}
                onChange={(e) => setNewSickReason(e.target.value)}
                placeholder="Grund (optional)"
              />
              <input
                type="number"
                value={newSickApprovedBy}
                onChange={(e) => setNewSickApprovedBy(e.target.value)}
                placeholder="Genehmigt von (ID, optional)"
              />
              <button type="button" onClick={handleAddSickLeave}>
                Krankmeldung hinzufügen
              </button>
            </div>
          </div>
          {/* Rechte Spalte: Tabelle der Krankmeldungen mit Paginierung */}
          <div className="col" style={{ flex: 2 }}>
            <h3>Erfasste Krankmeldungen</h3>
            {!isNew ? (
              <>
                {sickLeaves.length > 0 ? (
                  <>
                    <table style={{ width: '100%' }}>
                      <thead>
                        <tr>
                          <th>Startdatum</th>
                          <th>Enddatum</th>
                          <th>Grund</th>
                          <th>Genehmigt von</th>
                          <th>Aktionen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentSickLeaves.map(sick => (
                          <tr key={sick.id}>
                            <td>{new Date(sick.startDate).toLocaleDateString()}</td>
                            <td>{new Date(sick.endDate).toLocaleDateString()}</td>
                            <td>{sick.reason || '-'}</td>
                            <td>{sick.approvedBy || '-'}</td>
                            <td>
                              <button type="button" onClick={() => handleDeleteAbsence(sick.id, 'krank')}>
                                Löschen
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'space-between' }}>
                      <button
                        type="button"
                        onClick={() => setSickPage(prev => Math.max(prev - 1, 1))}
                        disabled={sickPage === 1}
                      >
                        Vorherige Seite
                      </button>
                      <span>
                        Seite {sickPage} von {totalSickPages}
                      </span>
                      <button
                        type="button"
                        onClick={() => setSickPage(prev => Math.min(prev + 1, totalSickPages))}
                        disabled={sickPage === totalSickPages}
                      >
                        Nächste Seite
                      </button>
                    </div>
                  </>
                ) : (
                  <p>Keine Krankmeldungen erfasst.</p>
                )}
              </>
            ) : (
              <p>Abwesenheiten können erst erfasst werden, wenn der Mitarbeiter gespeichert wurde.</p>
            )}
          </div>
        </div>
      )}

      <button type="submit" className="submit-button">
        {isNew ? 'Erstellen' : 'Aktualisieren'}
      </button>
    </form>
  );
};

export default EmployeeForm;
